import { Badge, Button, Card, Checkbox, Col, Flex, Form, Input, InputNumber, Popconfirm, Row, Space } from "antd";
import { useTranslation } from "react-i18next";
import { mdlReservationFilter } from "../../models/ui-models/ReservationFilterProps";
import { mdlReservationListRequest } from "../../models/service-models/reservation/ReservationListRequest";
import { HotelSelect } from "../common/HotelSelect";
import { useAppSelector } from "../../store/Hooks";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import _ from "lodash";
import { AgencySelectByHotelPaxId } from "../agencies/AgencySelectByHotelPaxId";
import { ConfirmStatusSelect } from "../common/ConfirmStatusSelect";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import { ReservationTypeSelect } from "../common/ReservationTypeSelect";
import { enmReservationStatus } from "../../models/enums/ReservationStatus";
import { enmReservationType } from "../../models/enums/ReservationType";
import { ReservationStatusSelect } from "../common/ReservationStatusSelect";
import { useMutation, useQuery } from "react-query";
import { mdlReservationFilterSaveRequest } from "../../models/service-models/reservation/ReservationFilterSaveRequest";
import { ReservationService } from "../../services/ReservationService";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import SFPopover from "../common/SFPopover";
import { DeleteOutlined, QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { mdlBaseIdRequest } from "../../models/service-models/common/BaseIdRequest";
import { SFDatePicker } from "../common/DatePicker";
import { mdlHotel } from "../../models/domain/Hotel";
import { mdlAgencyListByPaxIdRequest } from "../../models/service-models/agency/AgencyListByPaxIdRequest";
import { AgencyService } from "../../services/AgencyService";
import { mdlIdNameIndexPair } from "../../models/domain/IdNameIndexPair";


export const ReservationFilter = (props: mdlReservationFilter) => {
    const { t } = useTranslation();
    const [reset, setReset] = useState<boolean>(false);
    const [selectedHotels, setSelectedHotels] = useState<Array<string>>();
    const [agencies, setAgencies] = useState<Array<mdlIdNameIndexPair>>();
    const [form] = Form.useForm();
    const userHotels = useAppSelector(x => x.site.userHotels);
    const userInfo = useAppSelector(x => x.site.user);

    useEffect(() => {
        if (reset)
            form.resetFields();
        setSelectedHotels(props.form?.hotelIds);
    }, [reset])

    const { data, isLoading, refetch, isError } = useQuery(
        ["getFilters", userInfo?.userId],
        () => {
            return ReservationService.GetFilters({ currentLanguage: "en-US" });
        },
        {
            staleTime: 0,
            refetchOnWindowFocus: false,
            enabled: !!props,
        }
    );

    const handleHotelChange = (values: string[]) => {
        var foundList = _.filter(userHotels, function (h) { return values.includes(h.id) });
        form.setFieldsValue({ hotelIds: values, paximumIds: foundList.map(x => x.paximumId) });
        form.setFieldValue("agencyIds", []);
        setSelectedHotels(values);
    }

    const { mutateAsync: saveFilter, isLoading: mutateLoading, data: mutateData } = useMutation(
        async (data: mdlReservationFilterSaveRequest) => {
            return ReservationService.SaveFilter(data)
        },
        {
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    refetch();
                    toast.success(t("common.success"));
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const { mutateAsync: removeFilter, isLoading: removeMutateLoading, data: removeMutateData } = useMutation(
        async (data: mdlBaseIdRequest) => {
            return ReservationService.RemoveFilter(data)
        },
        {
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    refetch();
                    toast.success(t("common.success"));
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const {
        data: agenciesData,
        isLoading: agenciesLoading,
        isError: agenciesError,
        refetch: agenciesRefetch
    } = useQuery(
        ["getAgenciesByPaxId", selectedHotels],
        () => {
            if (selectedHotels != null && selectedHotels.length > 0 && userHotels) {
                var paxIds = _.filter(userHotels, function (hotel) {
                    return selectedHotels?.includes(hotel.id);
                }).map(x => (x as mdlHotel).paximumId)
                var request: mdlAgencyListByPaxIdRequest = { paxIds: paxIds, crossCheck: false };
                return AgencyService.ListByPaxId(request);
            }

        },
        {
            onSuccess(data) {
                form.setFieldValue("agencyIds", data?.body?.data.map(x => x.id));
            },
            refetchOnWindowFocus: false,
            enabled: !!selectedHotels
        }
    );


    const getSelectedHotelPaxIds = () => {
        if (selectedHotels) {
            return _.filter(userHotels, function (hotel) {
                return selectedHotels?.includes(hotel.id);
            }).map(x => x.paximumId);
        } else {
            return userHotels?.map(x => x.paximumId);
        }
    }

    const handleConfirm = (value: string) => {
        var filterSaveRequest: mdlReservationFilterSaveRequest;
        filterSaveRequest = {
            agencyIds: form.getFieldValue("agencyIds"),
            paximumIds: form.getFieldValue("paximumIds"),
            sellDateStarts: form.getFieldValue("sellBegin"),
            sellDateEnds: form.getFieldValue("sellEnd"),
            checkInStarts: form.getFieldValue("checkInBegin"),
            checkInEnds: form.getFieldValue("checkInEnd"),
            voucherNo: form.getFieldValue("voucher"),
            checkOutStarts: form.getFieldValue("checkOutBegin"),
            checkOutEnds: form.getFieldValue("checkOutEnd"),
            confirmationStatus: form.getFieldValue("confirmationStatuses"),
            confDateStarts: form.getFieldValue("confirmDateBegin"),
            confDateEnds: form.getFieldValue("confirmDateEnd"),
            reservationType: form.getFieldValue("reservationTypes"),
            reservationStatus: form.getFieldValue("reservationStatuses"),
            adultsStarts: form.getFieldValue("adultStart"),
            adultsEnds: form.getFieldValue("adultEnd"),
            childsStarts: form.getFieldValue("childStart"),
            childsEnds: form.getFieldValue("childEnd"),
            infantsEnds: form.getFieldValue("infantEnd"),
            infantsStarts: form.getFieldValue("infantStart"),
            paxEnds: form.getFieldValue("totalPaxEnd"),
            paxStarts: form.getFieldValue("totalPaxStart"),
            filterDesc: value,
            hasWarning: form.getFieldValue("hasWarning")
        }

        filterSaveRequest.confirmationStatus = filterSaveRequest.confirmationStatus?.map(x => Number(x));
        filterSaveRequest.reservationStatus = filterSaveRequest.reservationStatus?.map(x => Number(x));
        filterSaveRequest.reservationType = filterSaveRequest.reservationType?.map(x => Number(x));

        if (filterSaveRequest.paximumIds == null)
            toast.error("monitor.hotelSelectionRequired")
        else
            saveFilter(filterSaveRequest);
    }

    const handleInhouse = () => {
        var filterListRequest: mdlReservationListRequest;
        filterListRequest = {
            agencyIds: form.getFieldValue("agencyIds"),
            paximumIds: form.getFieldValue("paximumIds"),
            sellBegin: form.getFieldValue("sellBegin"),
            sellEnd: form.getFieldValue("sellEnd"),
            checkInBegin: form.getFieldValue("checkInBegin"),
            checkInEnd: form.getFieldValue("checkInEnd"),
            voucher: form.getFieldValue("voucher"),
            checkOutBegin: form.getFieldValue("checkOutBegin"),
            checkOutEnd: form.getFieldValue("checkOutEnd"),
            confirmationStatuses: form.getFieldValue("confirmationStatuses"),
            confirmDateBegin: form.getFieldValue("confirmDateBegin"),
            confirmDateEnd: form.getFieldValue("confirmDateEnd"),
            reservationTypes: form.getFieldValue("reservationTypes"),
            reservationStatuses: form.getFieldValue("reservationStatuses"),
            adultStart: form.getFieldValue("adultStart"),
            adultEnd: form.getFieldValue("adultEnd"),
            childStart: form.getFieldValue("childStart"),
            childEnd: form.getFieldValue("childEnd"),
            infantEnd: form.getFieldValue("infantEnd"),
            infantStart: form.getFieldValue("infantStart"),
            totalPaxEnd: form.getFieldValue("totalPaxEnd"),
            totalPaxStart: form.getFieldValue("totalPaxStart"),
            inHouse: props.form?.inHouse ? !props.form?.inHouse : true,
            hasWarning: form.getFieldValue("hasWarning")
        }

        filterListRequest.confirmationStatuses = filterListRequest.confirmationStatuses?.map(x => Number(x));
        filterListRequest.reservationStatuses = filterListRequest.reservationStatuses?.map(x => Number(x));
        filterListRequest.reservationTypes = filterListRequest.reservationTypes?.map(x => Number(x));

        if (filterListRequest.paximumIds == null)
            toast.error("monitor.hotelSelectionRequired")
        else
            props.onSearch && props.onSearch(filterListRequest);
    }

    const handleRemoveFilter = (id?: string) => {
        if (id) {
            removeFilter({ id: id });
        }
    }

    const getHotelIdsFromPaxId = (values?: number[]) => {
        if (values) {
            var foundList = _.filter(userHotels, function (h) { return values.includes(h.paximumId) });
            return foundList.map(x => x.id);
        }
        return [];
    }

    const handleSearchByIt = (filter: mdlReservationFilterSaveRequest) => {
        if (filter) {
            filter.hotelIds = getHotelIdsFromPaxId(filter.paximumIds);
            form.setFieldsValue(
                {
                    "hotelIds": filter.hotelIds,
                    "agencyIds": filter.agencyIds,
                    "paximumIds": filter.paximumIds,
                    "sellBegin": filter.sellDateStarts,
                    "sellEnd": filter.sellDateEnds,
                    "checkInBegin": filter.checkInStarts,
                    "checkInEnd": filter.checkInEnds,
                    "voucher": filter.voucherNo,
                    "checkOutBegin": filter.checkInStarts,
                    "checkOutEnd": filter.checkOutEnds,
                    "confirmationStatuses": filter.confirmationStatus,
                    "confirmDateBegin": filter.confDateStarts,
                    "confirmDateEnd": filter.confDateEnds,
                    "reservationTypes": filter.reservationType,
                    "reservationStatuses": filter.reservationStatus,
                    "adultStart": filter.adultsStarts,
                    "adultEnd": filter.adultsEnds,
                    "childStart": filter.childsStarts,
                    "childEnd": filter.childsEnds,
                    "infantEnd": filter.infantsEnds,
                    "infantStart": filter.infantsStarts,
                    "totalPaxEnd": filter.paxEnds,
                    "totalPaxStart": filter.paxStarts,
                    "hasWarning": filter.hasWarning
                }
            );
            var listRequest: mdlReservationListRequest = {
                adultEnd: filter.adultsEnds ?? undefined,
                adultStart: filter.adultsStarts ?? undefined,
                agencyIds: filter.agencyIds,
                checkInEnd: filter.checkInEnds ?? undefined,
                checkInBegin: filter.checkInStarts ?? undefined,
                checkOutEnd: filter.checkOutEnds ?? undefined,
                checkOutBegin: filter.checkOutStarts ?? undefined,
                childEnd: filter.childsEnds ?? undefined,
                childStart: filter.childsStarts ?? undefined,
                confirmDateBegin: filter.confDateStarts ?? undefined,
                confirmationStatuses: filter.confirmationStatus,
                confirmDateEnd: filter.confDateEnds ?? undefined,
                hotelIds: filter.hotelIds,
                infantEnd: filter.infantsEnds ?? undefined,
                infantStart: filter.infantsStarts ?? undefined,
                paximumIds: filter.paximumIds,
                reservationStatuses: filter.reservationStatus,
                reservationTypes: filter.reservationType,
                sellBegin: filter.sellDateStarts ?? undefined,
                sellEnd: filter.sellDateEnds ?? undefined,
                totalPaxEnd: filter.paxEnds ?? undefined,
                totalPaxStart: filter.paxStarts ?? undefined,
                voucher: filter.voucherNo ?? undefined,
                pageNumber: 1,
                hasWarning: filter.hasWarning ?? undefined
            }
            props.onSearch && props.onSearch(listRequest);
        }
    }

    const handleReset = () => {
        setReset(true);
    }

    return (
        <Card loading={mutateLoading || removeMutateLoading}>
            <Row gutter={16}>
                <Col span={19}>
                    <Form
                        form={form}
                        initialValues={!reset ? {
                            hotelIds: props.form?.hotelIds,
                            agencyIds: props.form?.agencyIds,
                            paximumIds: props.form?.paximumIds,
                            adultStart: props.form?.adultStart,
                            adultEnd: props.form?.adultEnd,
                            childStart: props.form?.childStart,
                            childEnd: props.form?.childEnd,
                            infantStart: props.form?.infantStart,
                            infantEnd: props.form?.infantEnd,
                            totalPaxStart: props.form?.totalPaxStart,
                            totalPaxEnd: props.form?.totalPaxEnd,
                            checkInBegin: props.form?.checkInBegin,
                            checkInEnd: props.form?.checkInEnd,
                            sellBegin: props.form?.sellBegin,
                            sellEnd: props.form?.sellEnd,
                            voucher: props.form?.voucher,
                            checkOutBegin: props.form?.checkOutBegin,
                            checkOutEnd: props.form?.checkOutEnd,
                            confirmationStatuses: props.form?.confirmationStatuses?.map(x => x.toString()),
                            confirmDateBegin: props.form?.confirmDateBegin,
                            confirmDateEnd: props.form?.confirmDateEnd,
                            reservationTypes: props.form?.reservationTypes?.map(x => x.toString()),
                            reservationStatuses: props.form?.reservationStatuses?.map(x => x.toString()),
                            hasWarning: props.form?.hasWarning
                        } : {}}
                        name="resmon-filter-form"
                        layout="vertical"
                        onFinish={(values) => props.onSearch && props.onSearch(values)}
                        autoComplete="off"
                    >
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="paximumIds" hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item<mdlReservationListRequest>
                                    label={t("monitor.hotels")}
                                    name="hotelIds"
                                    rules={[{ required: true, message: t("common.required") }]}
                                >
                                    <HotelSelect maxTagCount={1} defaultAllSelected={!reset ? true : false} mode="multiple" onChange={handleHotelChange} />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="sellBegin" label={t("monitor.sellBegin")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("sellBegin", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("sellBegin", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="sellEnd" label={t("monitor.sellEnd")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("sellEnd", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("sellEnd", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label={t("monitor.adultStart")}>
                                    <Space.Compact>
                                        <Form.Item
                                            name={"adultStart"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            name={"adultEnd"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item<mdlReservationListRequest>
                                    label={t("monitor.agencies")}
                                    name="agencyIds"
                                    rules={[{ required: true, message: t("common.required") }]}
                                >
                                    <AgencySelectByHotelPaxId dataSource={agenciesData?.body?.data} maxTagCount={3} reset={reset} hotelIds={form.getFieldValue("hotelIds")} mode="multiple" onChange={(values: string[]) => { form.setFieldValue("agencyIds", values) }} />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="checkInBegin" label={t("monitor.checkInBegin")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("checkInBegin", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("checkInBegin", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="checkInEnd" label={t("monitor.checkInEnd")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("checkInEnd", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("checkInEnd", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label={t("monitor.childStart")}>
                                    <Space.Compact>
                                        <Form.Item
                                            name={"childStart"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            name={"childEnd"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item<mdlReservationListRequest>
                                    label={t("monitor.voucher")}
                                    name="voucher"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="checkOutBegin"
                                    label={t("monitor.checkOutBegin")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("checkOutBegin", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("checkOutBegin", null);
                                            }
                                        }} />
                                </Form.Item>

                            </Col>
                            <Col span={5}>
                                <Form.Item name="checkOutEnd" label={t("monitor.checkOutEnd")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("checkOutEnd", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("checkOutEnd", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label={t("monitor.infantStart")}>
                                    <Space.Compact>
                                        <Form.Item
                                            name={"infantStart"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            name={"infantEnd"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>

                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item<mdlReservationListRequest>
                                    label={t("monitor.confirmationStatus")}
                                    name="confirmationStatuses"
                                >
                                    <ConfirmStatusSelect specificTypes={[enmConfirmationStatus.No, enmConfirmationStatus.Yes, enmConfirmationStatus.Wait]} maxTagCount={2} mode="multiple" />
                                </Form.Item>
                            </Col>

                            <Col span={5}>
                                <Form.Item name="confirmDateBegin" label={t("monitor.confirmDateBegin")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("confirmDateBegin", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("confirmDateBegin", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={5}>
                                <Form.Item name="confirmDateEnd" label={t("monitor.confirmDateEnd")}>
                                    <SFDatePicker
                                        onChange={(date: Dayjs) => {
                                            if (date) {
                                                form.setFieldValue("confirmDateEnd", new Date(
                                                    Date.UTC(
                                                        date.year(),
                                                        date.month(),
                                                        date.date()
                                                    )
                                                ));
                                            } else {
                                                form.setFieldValue("confirmDateEnd", null);
                                            }
                                        }} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label={t("monitor.totalPaxStart")}>
                                    <Space.Compact>
                                        <Form.Item
                                            name={"totalPaxStart"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            name={"totalPaxEnd"}
                                            noStyle
                                        >
                                            <InputNumber min={0} style={{ width: '33%' }} />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>                            
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item<mdlReservationListRequest>
                                    label={t("monitor.reservationType")}
                                    name="reservationTypes"
                                >
                                    <ReservationTypeSelect maxTagCount={2} mode="multiple" defaultValue={!reset ? [enmReservationType.New.toString(), enmReservationType.Changed.toString(), enmReservationType.Cancelled.toString()] : []} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item<mdlReservationListRequest>
                                    label={t("monitor.reservationStatus")}
                                    name="reservationStatuses"
                                >
                                    <ReservationStatusSelect maxTagCount={2} mode="multiple" defaultValue={!reset ? [enmReservationStatus.Ok.toString(), enmReservationStatus.OnRequest.toString(), enmReservationStatus.NoShow.toString(), enmReservationStatus.GoShow.toString(), enmReservationStatus.Option.toString()] : []} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item<mdlReservationListRequest> label={t("monitor.warnings")} name="hasWarning">
                                    <Checkbox defaultChecked={props.form?.hasWarning} onChange={(e: any) => form.setFieldValue("hasWarning", e.target.checked)}  >{t("monitor.showOnlyWarnings")}</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit">{t("common.search")}</Button>
                                    <Badge color="green" dot={props.form?.inHouse ? true : false}>
                                        <Button type="primary" onClick={handleInhouse} style={{ backgroundColor: "purple" }} >{t("monitor.inhouse")}</Button>
                                    </Badge>
                                    <SFPopover onConfirm={handleConfirm} />
                                    <Button onClick={handleReset} type="primary" style={{ backgroundColor: "red" }}>{t("monitor.clear")}</Button>
                                </Space>

                            </Form.Item>
                        </Row>
                    </Form>
                </Col>
                <Col span={5}>
                    <Card bordered={false} loading={isLoading}>
                        <strong>{t("monitor.savedFilters")}</strong>
                        <Flex vertical style={{ gap: "15px" }}>
                            {
                                data?.body?.data && data.body.data.length > 0 ? data?.body?.data?.map((filter: mdlReservationFilterSaveRequest, index: number) => (
                                    <Row key={index}>
                                        <Col span={8}>
                                            <label>{filter.filterDesc}</label>
                                        </Col>
                                        <Col span={8}>
                                            <Space>
                                                <Button onClick={() => handleSearchByIt(filter)}
                                                    type="primary" icon={<SearchOutlined />} size="middle">
                                                </Button>
                                                <Popconfirm
                                                    title={t("common.deleteWarning")}
                                                    description={t("common.deleteDescription")}
                                                    okText={t("common.okConfirm")}
                                                    cancelText={t("common.cancelConfirm")}
                                                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                    onConfirm={() => handleRemoveFilter(filter.id)}
                                                >
                                                    <Button icon={<DeleteOutlined />} type="primary" danger>
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                        </Col>
                                    </Row>
                                ))
                                    :
                                    <Row>
                                        <label>{t("monitor.noFilter")}</label>
                                    </Row>
                            }
                        </Flex>
                    </Card>
                </Col>
            </Row>
        </Card >
    );
}